import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, TreePine, Sprout, Fence } from 'lucide-react';
import { services, galleryImages } from '../data/mockData';
import { SiteVideo } from '../components/SiteVideo';

const Creation = () => {
  const creationImages = galleryImages.filter(img => img.category === 'creation');
  
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-red-50 via-white to-red-50">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Texte à gauche */}
            <div>
              <div className="inline-block mb-6 px-4 py-2 bg-red-100 rounded-full">
                <span className="text-red-700 font-semibold text-sm">Service</span>
              </div>
              <h1 data-testid="creation-page-title" className="text-4xl md:text-6xl font-bold mb-6 text-red-600">
                {services.creation.title}
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-6">
                {services.creation.description}
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                {services.creation.details}
              </p>
            </div>

            {/* Vidéo à droite */}
            <div className="flex items-center justify-center">
              <SiteVideo name="creation" label="Création paysagère Perinature" />
            </div>
          </div>
        </div>
      </section>

      {/* Details supprimée - déjà dans le hero */}
      <section className="py-0"></section>

      {/* Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-red-50">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
            Nos prestations de création
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.creation.features.map((feature, index) => (
              <div
                key={index}
                data-testid={`creation-feature-${index}`}
                className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-red-100 hover:border-red-300 transition-all duration-300 hover:shadow-lg"
              >
                <CheckCircle className="text-red-500 flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-700 font-medium">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialties */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
            Notre expertise
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <TreePine className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Plantation</h3>
              <p className="text-gray-600">
                Sélection et plantation d'arbres, arbustes et plantes adaptés au climat du Périgord.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sprout className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Aménagement</h3>
              <p className="text-gray-600">
                Création de massifs, rocailles, potagers et espaces fleuris sur mesure.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Fence className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Infrastructure</h3>
              <p className="text-gray-600">
                Terrasses, allées, clôtures, portails et systèmes d'arrosage automatique.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {creationImages.length > 0 && (
        <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
              Réalisations de création
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {creationImages.map((image) => (
                <div
                  key={image.id}
                  data-testid={`creation-gallery-item-${image.id}`}
                  className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <img
                    src={image.url}
                    data-testid={`creation-gallery-image-${image.id}`}
                    alt={image.title}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="font-bold text-lg">{image.title}</h3>
                      <p className="text-sm text-gray-200">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Expertise Badge */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-pink-50 to-white p-8 rounded-2xl border-2 border-pink-100">
            <h3 className="text-2xl font-bold mb-4 text-gray-900 text-center">
              Plus de 20 ans d'expérience
            </h3>
            <p className="text-gray-700 text-center leading-relaxed">
              Depuis 2001, nous mettons notre savoir-faire au service de vos projets. Chaque aménagement est unique et réalisé avec le plus grand soin. Nous utilisons des matériaux durables et des végétaux de qualité pour garantir la longue vie de vos espaces verts.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-red-500 to-red-600">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">
            Donnez vie à votre projet
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour discuter de votre projet de création paysagère.
          </p>
          <Link
            to="/contact"
            data-testid="creation-contact-link"
            className="inline-block px-8 py-4 bg-white text-red-600 rounded-lg font-semibold hover:bg-red-50 transition-all duration-200 hover:scale-105 shadow-xl"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Creation;